import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Button } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { Card } from '../../../Shared/components/Card';
import styles from './styles.module.css';
import { formatPhoneNumber, EMDASH } from '../../../Shared/utils';
import { CardSectionInfoPairs } from 'Shared/components/Card/CardSectionInfoPairs';
import { useLazyBaseApi } from 'Shared/services/Api';
import { AttributionSearchType } from 'Admin/services/endpoints/PostAttributionSearch';
import { initialState } from 'Admin/Partners/screens/Reattribution';
import { reattributePolicies } from 'Admin/authPolicies';
import { capitalizeFirstLetter } from 'Policies/utils/helpers';

type PolicyProducerInfoType = {
  producer?: {
    producerName: string;
    agency: string;
    email: string;
    phone: string;
    agencyId: string;
  };
  policyNumber?: string;
  policyBindReferenceId?: string;
};

export const PolicyProducerInfo: FC<PolicyProducerInfoType> = ({
  producer,
  policyNumber,
  policyBindReferenceId,
}) => {
  const { user } = useAuth0();
  const history = useHistory();
  const [{ loading }, fetchPolicyData] = useLazyBaseApi<AttributionSearchType>({
    method: 'POST',
    url: 'policies/attribution/search',
  });
  const canReattributePolicies = reattributePolicies(user!.effectiveGrants);
  const loaded = !!producer && !!policyNumber;
  return (
    <Card>
      <h2>Producer Info</h2>
      <div className={classNames('fs-exclude', styles.data)}>
        <CardSectionInfoPairs
          headers={[
            'Producer',
            'Agency',
            'Email Address',
            'Phone number',
            'Bind Reference ID',
          ]}
          dataFontStyle="none"
          data={
            loaded
              ? [
                  capitalizeFirstLetter(producer.producerName) || EMDASH,
                  capitalizeFirstLetter(producer.agency),
                  producer.email && producer.email !== 'Not available'
                    ? {
                        display: (
                          <a
                            style={{ textTransform: 'none' }}
                            href={`mailto:${producer.email}`}
                          >
                            {producer.email}
                          </a>
                        ),
                        value: producer.email,
                      }
                    : EMDASH,
                  formatPhoneNumber(producer.phone) || EMDASH,
                  policyBindReferenceId,
                ]
              : Array(4).fill(4)
          }
          className={styles.infoPairs}
          loading={producer === undefined}
        />
        {loaded && canReattributePolicies && (
          <Button
            data-testid="policy-reattribution-button"
            className={styles.reattributeButton}
            disabled={!producer.agencyId}
            isSubmitting={loading}
            onClick={async () => {
              try {
                const reattributionPolicyNumber = policyNumber.split('-')[0];
                const policyDataResponse = await fetchPolicyData({
                  data: {
                    filters: {
                      attributedPartnerId: producer.agencyId,
                      policyNumber: reattributionPolicyNumber,
                    },
                  },
                });

                if (policyDataResponse.data) {
                  const reattributionState = {
                    ...initialState,
                    policies: {
                      ...initialState.policies,
                      selections: [
                        policyDataResponse.data.policies[0].universalPolicyId,
                      ],
                      data: policyDataResponse.data.policies,
                    },
                    filters: {
                      ...initialState.filters,
                      policyNumber: reattributionPolicyNumber,
                    },
                  };

                  history.push({
                    pathname: `/admin/partners/${producer.agencyId}/reattribution/confirm`,
                    state: {
                      initialState: reattributionState,
                    },
                  });
                }
              } catch (err) {
                console.error(err);
              }
            }}
          >
            Reattribute Policy
          </Button>
        )}
      </div>
    </Card>
  );
};
